import { Fragment, type MouseEvent } from 'react'

import { opensInPreview } from '@/components/AgentChat/previewDocument'
import { Spinner } from '@/components/ui/spinner'
import { TableCell, TableRow } from '@/components/ui/table'
import { DEFAULT_FILE_ICON, FILE_ICONS, fileExtension } from '@/lib/fileIcon'
import { cn } from '@/lib/utils'
import { documentTypeLabel, PARSE_PENDING } from '@/models/document'
import { DocumentActions } from './DocumentActions'
import { expiryClass, formatDate, formatFileSize } from './format'
import {
  attachmentUrl,
  type DocumentsTableAttachment,
  type DocumentsTableDocument,
  totalFileSize,
  uploadedAt,
} from './types'
import type { DocumentRowActions } from './useDocumentRowActions'

type PreviewAction = Pick<DocumentRowActions, 'openPreview'>

/** With `openPreview`, an image, PDF or spreadsheet opens in the preview; Cmd/Ctrl-click, or any other file, opens it in a new tab. */
export function AttachmentLink({
  document,
  attachment,
  label = attachment.fileName,
  openPreview,
  className,
}: {
  document: DocumentsTableDocument
  attachment: DocumentsTableAttachment
  /** Also the preview's heading. */
  label?: string
  className?: string
} & PreviewAction) {
  const url = attachmentUrl(attachment)

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!openPreview || !opensInPreview(event, attachment.fileName)) return
    event.preventDefault()
    openPreview(
      { documentId: document.id, attachmentId: attachment.id, fileName: attachment.fileName },
      label
    )
  }

  if (!url) {
    return <span className={cn('text-muted-foreground font-medium', className)}>{label}</span>
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={cn('font-medium text-blue-600 hover:underline', className)}
    >
      {label}
    </a>
  )
}

/** Tighter than the shared table's default, so a long list stays scannable. */
export const COMPACT_ROW = '[&>td]:py-1.5'

/** Only a single-file document can point its name at a file; the rest list theirs below. */
export function soleAttachment(document: DocumentsTableDocument): DocumentsTableAttachment | null {
  return document.attachments.length === 1 ? document.attachments[0] : null
}

/** Whatever the name is not already saying about the files underneath it. */
export function documentSubline(document: DocumentsTableDocument): string {
  const single = soleAttachment(document)
  if (single) return single.fileName === document.displayName ? '' : single.fileName
  if (!document.attachments.length) return 'No attachments'
  return `${document.attachments.length} files`
}

function DocumentName({ document, openPreview }: { document: DocumentsTableDocument } & PreviewAction) {
  const single = soleAttachment(document)
  const subline = documentSubline(document)
  const Icon = FILE_ICONS[fileExtension(single?.fileName)] ?? DEFAULT_FILE_ICON

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className="text-muted-foreground size-4 shrink-0" />
      <div className="flex min-w-0 flex-col leading-tight">
        {single ? (
          <AttachmentLink
            document={document}
            attachment={single}
            label={document.displayName}
            openPreview={openPreview}
            className="truncate"
          />
        ) : (
          <span className="truncate font-medium">{document.displayName}</span>
        )}
        {subline && <span className="text-muted-foreground truncate text-xs">{subline}</span>}
      </div>
    </div>
  )
}

/** One file of a multi-file document, dated by its own upload rather than the document's newest. */
function AttachmentRow({
  document,
  attachment,
  openPreview,
}: {
  document: DocumentsTableDocument
  attachment: DocumentsTableAttachment
} & PreviewAction) {
  const Icon = FILE_ICONS[fileExtension(attachment.fileName)] ?? DEFAULT_FILE_ICON

  return (
    <TableRow className={COMPACT_ROW}>
      <TableCell className="w-full max-w-0 min-w-40 pl-8">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="text-muted-foreground size-4 shrink-0" />
          <AttachmentLink
            document={document}
            attachment={attachment}
            openPreview={openPreview}
            className="truncate"
          />
        </div>
      </TableCell>
      <TableCell colSpan={3} />
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {formatFileSize(attachment.fileSize)}
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {formatDate(attachment.dateCreated ?? uploadedAt(document))}
      </TableCell>
      <TableCell />
    </TableRow>
  )
}

/** One row per document; a document holding more than one file lists them underneath. */
export function DocumentRow({
  document,
  openDetails,
  openRename,
  openPreview,
}: { document: DocumentsTableDocument } & DocumentRowActions) {
  const isPending = document.parseStatus === PARSE_PENDING

  return (
    <Fragment>
      <TableRow className={COMPACT_ROW}>
        <TableCell className="w-full max-w-0 min-w-40">
          <DocumentName document={document} openPreview={openPreview} />
        </TableCell>
        <TableCell className="text-muted-foreground whitespace-nowrap">
          {documentTypeLabel(document.documentType)}
        </TableCell>
        {isPending ? (
          <TableCell colSpan={2} className="text-muted-foreground whitespace-nowrap">
            <span className="flex items-center gap-2 text-sm">
              <Spinner className="size-3.5" />
              Reading document…
            </span>
          </TableCell>
        ) : (
          <>
            <TableCell className="text-muted-foreground whitespace-nowrap">
              {formatDate(document.dateEffective)}
            </TableCell>
            <TableCell className={cn('whitespace-nowrap', expiryClass(document.dateExpiration))}>
              {formatDate(document.dateExpiration)}
            </TableCell>
          </>
        )}
        <TableCell className="text-muted-foreground whitespace-nowrap">
          {formatFileSize(totalFileSize(document))}
        </TableCell>
        <TableCell className="text-muted-foreground whitespace-nowrap">
          {formatDate(uploadedAt(document))}
        </TableCell>
        <TableCell className="text-right">
          <DocumentActions document={document} openDetails={openDetails} openRename={openRename} />
        </TableCell>
      </TableRow>
      {document.attachments.length > 1 &&
        document.attachments.map((attachment) => (
          <AttachmentRow
            key={attachment.id}
            document={document}
            attachment={attachment}
            openPreview={openPreview}
          />
        ))}
    </Fragment>
  )
}

export function DocumentRows({
  documents,
  openDetails,
  openRename,
  openPreview,
}: { documents: DocumentsTableDocument[] } & DocumentRowActions) {
  return (
    <>
      {documents.map((document) => (
        <DocumentRow
          key={document.id}
          document={document}
          openDetails={openDetails}
          openRename={openRename}
          openPreview={openPreview}
        />
      ))}
    </>
  )
}
